import type { BoardSlug, PostListResponse, PostSummary } from '@tomato/shared';
import {
  BOARD_LABELS,
  BOARD_TRADE_TYPES,
  formatPrice,
  isMarketBoard,
  isTradeClosed,
  TALK_CATEGORIES,
  TALK_CATEGORY_LABELS,
  TRADE_PLACE_LABELS,
  tradeStatusLabel,
  TRADE_TYPE_LABELS,
} from '@tomato/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { api, ApiError } from '../api/client';
import { TomatoHuntIcon } from '../components/TomatoHunt';
import {
  Badge,
  Button,
  EmptyState,
  Notice,
  PostListSkeleton,
  Skeleton,
  TextField,
} from '../components/ui';
import { formatWhen } from '../lib/markdown';
import { useRefreshStore } from '../store/refresh';
import { useSessionStore } from '../store/session';

export function PostRow({ post }: { post: PostSummary }) {
  // 완료된 거래는 흐리게 + 취소선. 지우지 않는 이유는 거래 맥락이 남아야 해서다(PRD 5절).
  const closed = isTradeClosed(post.tradeStatus);

  return (
    <div className="relative">
      {/* 오늘의 토마토가 이 카드에 숨었다면 우상단에 살짝 뜬다(Link 밖 형제 → 클릭이 겹치지 않음). */}
      <TomatoHuntIcon elementId={`post-${post.id}`} className="absolute right-1 top-3 z-10" />
      <Link
        viewTransition
        to={`/posts/${post.id}`}
        className={`flex gap-3 px-1 py-4 transition active:opacity-60 sm:hover:opacity-70 ${
          closed ? 'opacity-45' : ''
        }`}
      >
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          {post.category && <Badge>{TALK_CATEGORY_LABELS[post.category]}</Badge>}
          {post.tradeType && <Badge>{TRADE_TYPE_LABELS[post.tradeType]}</Badge>}
          {/* 판매중/구하는 중은 기본 상태라 뱃지를 달지 않는다. 예약중만 눈에 띄면 된다. */}
          {post.tradeStatus === 'reserved' && (
            <Badge tone="accent">{tradeStatusLabel(post.tradeStatus, post.tradeType)}</Badge>
          )}
          <h2
            className={`min-w-0 flex-1 truncate text-[15px] font-medium text-ink-900 ${
              closed ? 'line-through' : ''
            }`}
          >
            {post.title}
          </h2>
          {/* 가격·거래 장소는 제목 바로 옆에 붙인다 — 제목에 제품명이 오니 한 줄로 읽힌다. */}
          {formatPrice(post.price, post.tradeType) && (
            <span className="shrink-0 whitespace-nowrap text-[14px] font-semibold text-ink-900">
              {formatPrice(post.price, post.tradeType)}
            </span>
          )}
          {post.tradePlace && (
            <span className="shrink-0 whitespace-nowrap text-[12px] text-ink-400">
              {TRADE_PLACE_LABELS[post.tradePlace]}
            </span>
          )}
        </div>

        {post.excerpt && (
          <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-ink-400">
            {post.excerpt}
          </p>
        )}

        <div className="mt-2 flex items-center gap-1.5 text-[12px] text-ink-400">
          <span className="text-ink-500">{post.author.nickname}</span>
          <span aria-hidden>·</span>
          <span>{formatWhen(post.createdAt)}</span>
          {/* 조회·사진·추천은 모바일에서 접는다 — 프리뷰는 간결하게(제목·가격·꼬리 최소). */}
          {post.viewCount > 0 && (
            <span className="max-sm:hidden">
              <span aria-hidden>· </span>조회 {post.viewCount}
            </span>
          )}
          {post.imageCount > 0 && (
            <span className="max-sm:hidden">
              <span aria-hidden>· </span>사진 {post.imageCount}
            </span>
          )}
          {post.commentCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>댓글 {post.commentCount}</span>
            </>
          )}
          {post.likeCount > 0 && (
            <span className="max-sm:hidden">
              <span aria-hidden>· </span>추천 {post.likeCount}
            </span>
          )}
          {post.favoriteCount > 0 && (
            <>
              <span aria-hidden>·</span>
              <span>찜 {post.favoriteCount}</span>
            </>
          )}
        </div>
      </div>

      {post.thumbnailUrl && (
        <img
          src={post.thumbnailUrl}
          alt=""
          loading="lazy"
          className="size-16 shrink-0 rounded-xl bg-ink-100 object-cover"
        />
      )}
      </Link>
    </div>
  );
}

/**
 * 사진으로 보기 카드 — 사진을 크게 깔고 제목·가격을 바로 아래 붙인다.
 * 목록(PostRow)이 "훑어보기"라면 이쪽은 "쇼핑하듯 고르기"가 목적이라 정보를 셋(사진·제목·가격)으로 줄였다.
 */
export function PostCard({ post }: { post: PostSummary }) {
  const closed = isTradeClosed(post.tradeStatus);
  const price = formatPrice(post.price, post.tradeType);

  return (
    <div className="relative">
      <TomatoHuntIcon elementId={`post-${post.id}`} className="absolute right-2 top-2 z-10" />
      <Link
        viewTransition
        to={`/posts/${post.id}`}
        className="block transition active:opacity-60 sm:hover:opacity-85"
      >
        <div className="relative aspect-square overflow-hidden rounded-2xl bg-ink-100">
          {post.thumbnailUrl ? (
            <img
              src={post.thumbnailUrl}
              alt=""
              loading="lazy"
              className={`size-full object-cover ${closed ? 'opacity-40' : ''}`}
            />
          ) : (
            // 사진 없는 글도 숨기지 않는다 — 안 보이면 쓴 사람이 이유를 알 수 없다.
            <div className="flex size-full items-center justify-center text-[28px] opacity-30">🍅</div>
          )}
          {/* 상태는 사진 위에 얹는다 — 카드가 작아서 아래로 내리면 제목·가격을 밀어낸다. */}
          {closed && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-ink-900/75 px-3 py-1 text-[13px] font-medium text-ink-50">
                {tradeStatusLabel(post.tradeStatus, post.tradeType)}
              </span>
            </div>
          )}
          {!closed && post.tradeStatus === 'reserved' && (
            <span className="absolute left-2 top-2 rounded-full bg-tomato-600 px-2.5 py-1 text-[12px] font-medium text-white">
              {tradeStatusLabel(post.tradeStatus, post.tradeType)}
            </span>
          )}
        </div>

        <h2
          // 2줄 자리를 늘 비워둔다 — 제목 길이가 제각각이면 가격 줄이 열마다 어긋나 그리드가 지저분해진다.
          className={`mt-2.5 line-clamp-2 min-h-[2.75em] text-[14px] leading-snug text-ink-700 ${
            closed ? 'line-through' : ''
          }`}
        >
          {post.title}
        </h2>

        {/* 가격은 사진 바로 아래 가장 큰 글씨 — 고를 때 제일 먼저 보는 값이다. */}
        {price && (
          <p
            className={`mt-1 text-[17px] font-semibold ${
              post.tradeType === 'free' ? 'text-tomato-600' : 'text-ink-900'
            }`}
          >
            {price}
          </p>
        )}

        {(post.tradePlace || post.favoriteCount > 0) && (
          <div className="mt-1 flex items-center gap-1.5 text-[12px] text-ink-400">
            {post.tradePlace && <span>{TRADE_PLACE_LABELS[post.tradePlace]}</span>}
            {post.tradePlace && post.favoriteCount > 0 && <span aria-hidden>·</span>}
            {post.favoriteCount > 0 && <span>찜 {post.favoriteCount}</span>}
          </div>
        )}
      </Link>
    </div>
  );
}

function PostGridSkeleton({ cards = 6 }: { cards?: number }) {
  return (
    <div className={GRID_CLASS} role="status" aria-label="불러오는 중">
      {Array.from({ length: cards }, (_, i) => (
        <div key={i}>
          <Skeleton className="aspect-square w-full rounded-2xl" />
          <Skeleton className="mt-2.5 h-4 w-11/12" />
          <Skeleton className="mt-1.5 h-5 w-2/5" />
        </div>
      ))}
    </div>
  );
}

const GRID_CLASS = 'grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 lg:grid-cols-4';

/** 보기 모드는 취향이라 기기에 남긴다 — 글 보고 뒤로 왔을 때 목록으로 되돌아가면 짜증난다. */
const VIEW_KEY = 'tomato.boardView';
type BoardView = 'list' | 'photo';

export function BoardScreen() {
  const { slug } = useParams<{ slug: BoardSlug }>();
  const navigate = useNavigate();
  const me = useSessionStore((s) => s.me);
  // 계열사를 공개한 사람만 이 필터를 쓸 수 있다(서버가 최종 판정, 여기선 노출 자체를 막는다).
  const canFilterAffiliation = Boolean(me?.affiliation && me.affiliationVisible);
  // 알립니다(공지)는 관리자만 쓴다. 나머지 게시판은 누구나.
  const canWrite = slug !== 'notice' || me?.role === 'admin';

  const [storedView, setStoredView] = useState<BoardView>(
    () => (localStorage.getItem(VIEW_KEY) === 'photo' ? 'photo' : 'list'),
  );
  // 거래형 게시판(장터·책방)이 아닌 곳에서는 저장된 값이 photo여도 목록으로 본다
  // (탭 자체가 없으니 갈아탈 방법도 없다).
  const marketLike = Boolean(slug && isMarketBoard(slug));
  const view: BoardView = marketLike ? storedView : 'list';
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [mineOnly, setMineOnly] = useState(false);
  /** 카테고리 토글 — market이면 거래유형, talk면 카테고리. ''=전체. */
  const [filterCat, setFilterCat] = useState('');
  const [queryInput, setQueryInput] = useState('');
  const [query, setQuery] = useState('');

  // 게시판이 바뀌면 카테고리 선택은 의미가 없어진다(보드마다 값이 다름).
  useEffect(() => {
    setFilterCat('');
  }, [slug]);
  const [items, setItems] = useState<PostSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(false);
  const [error, setError] = useState('');
  // 당겨서 새로고침 — tick이 오르면 첫 페이지부터 다시 불러온다.
  const refreshTick = useRefreshStore((s) => s.tick);

  const fetchPage = useCallback(
    async (next: string | null) => {
      // 정렬 탭은 없앴다(보기 모드로 대체). 서버 기본값(활발순)을 그대로 쓴다.
      const params = new URLSearchParams();
      if (onlyOpen) params.set('open', '1');
      if (mineOnly) params.set('affiliation', 'mine');
      if (filterCat) params.set(marketLike ? 'type' : 'category', filterCat);
      if (query) params.set('q', query);
      if (next) params.set('cursor', next);
      return api.get<PostListResponse>(`/api/boards/${slug}/posts?${params}`);
    },
    [slug, onlyOpen, mineOnly, filterCat, query],
  );

  // 타이핑할 때마다 검색하면 요청이 쏟아진다. 멈춘 뒤에 보낸다.
  useEffect(() => {
    const t = setTimeout(() => setQuery(queryInput.trim()), 350);
    return () => clearTimeout(t);
  }, [queryInput]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setItems([]);
    setCursor(null);

    void fetchPage(null)
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setCursor(res.nextCursor);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : '목록을 불러오지 못했어요.');
      })
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [fetchPage, refreshTick]);

  async function loadMore() {
    if (!cursor || more) return;
    setMore(true);
    try {
      const res = await fetchPage(cursor);
      setItems((prev) => [...prev, ...res.items]);
      setCursor(res.nextCursor);
    } catch {
      setError('더 불러오지 못했어요.');
    } finally {
      setMore(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between py-6">
        <h1 className="text-[26px] font-semibold tracking-tight text-ink-900">
          {slug && BOARD_LABELS[slug]}
        </h1>
        {canWrite && (
          <Button size="sm" onClick={() => navigate(`/${slug}/write`)}>
            글쓰기
          </Button>
        )}
      </div>

      {/* 카테고리 토글 — 검색창이 있던 자리. 검색은 목록 하단으로 내려갔다.
          스크롤해도 상단 헤더 바로 아래 고정(sticky) — 목록을 내리다가도 바로 갈아탈 수 있게.
          헤더 높이: 모바일 h-14(56px), 데스크톱은 게시판 탭 줄까지 96px. */}
      {(marketLike || slug === 'talk') && (
        <div className="sticky top-14 z-10 -mx-5 mb-3 flex gap-1 overflow-x-auto bg-ink-50/95 px-5 py-2 backdrop-blur-xl sm:top-24">
          <button
            onClick={() => setFilterCat('')}
            className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
              filterCat === '' ? 'bg-tomato-600 text-white' : 'bg-ink-50 text-ink-500 hover:bg-ink-100'
            }`}
          >
            전체
          </button>
          {(slug && isMarketBoard(slug) ? BOARD_TRADE_TYPES[slug] : TALK_CATEGORIES).map((code) => (
            <button
              key={code}
              onClick={() => setFilterCat((v) => (v === code ? '' : code))}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
                filterCat === code
                  ? 'bg-tomato-600 text-white'
                  : 'bg-ink-50 text-ink-500 hover:bg-ink-100'
              }`}
            >
              {marketLike
                ? TRADE_TYPE_LABELS[code as keyof typeof TRADE_TYPE_LABELS]
                : TALK_CATEGORY_LABELS[code as (typeof TALK_CATEGORIES)[number]]}
            </button>
          ))}
        </div>
      )}

      <div className="mb-4 flex items-center gap-1">
        {/* 사진 탭은 장터에서만. 수다·알립니다는 사진 없는 글이 대부분이라 빈 카드 벽이 되고, 가격도 없다. */}
        {(marketLike
          ? ([
              ['list', '전체 보기'],
              ['photo', '사진으로 보기'],
            ] as const)
          : ([['list', '전체 보기']] as const)
        ).map(([code, label]) => (
          <button
            key={code}
            onClick={() => {
              setStoredView(code);
              localStorage.setItem(VIEW_KEY, code);
            }}
            className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
              view === code ? 'bg-ink-900 text-ink-50' : 'text-ink-500 hover:bg-ink-100'
            }`}
          >
            {label}
          </button>
        ))}
        {canFilterAffiliation && (
          <button
            onClick={() => setMineOnly((v) => !v)}
            className={`ml-auto rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
              mineOnly ? 'bg-tomato-600 text-white' : 'text-ink-500 hover:bg-ink-100'
            }`}
          >
            우리 계열사
          </button>
        )}
        {!canFilterAffiliation && <span className="ml-auto" />}
        {marketLike && (
          <button
            onClick={() => setOnlyOpen((v) => !v)}
            className={`rounded-full px-3 py-1.5 text-[13px] font-medium transition ${
              onlyOpen ? 'bg-tomato-600 text-white' : 'text-ink-500 hover:bg-ink-100'
            }`}
          >
            거래중만
          </button>
        )}
      </div>

      {loading ? (
        view === 'photo' ? (
          <PostGridSkeleton />
        ) : (
          <PostListSkeleton />
        )
      ) : items.length === 0 ? (
        <EmptyState
          icon={query ? '🔍' : '🍅'}
          title={query ? '검색 결과가 없어요' : '아직 글이 없어요'}
          body={query ? '다른 말로 찾아보세요.' : '첫 글을 남겨보세요.'}
        />
      ) : view === 'photo' ? (
        <div className={GRID_CLASS}>
          {items.map((post) => (
            <PostCard key={post.id} post={post} />
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl bg-surface px-4 shadow-card">
          {items.map((post, i) => (
            <div key={post.id}>
              {i > 0 && <div className="h-px bg-ink-100" />}
              <PostRow post={post} />
            </div>
          ))}
        </div>
      )}

      <Notice>{error}</Notice>

      {cursor && (
        <div className="mt-5 text-center">
          <Button variant="secondary" disabled={more} onClick={() => void loadMore()}>
            {more ? '불러오는 중…' : '더 보기'}
          </Button>
        </div>
      )}

      {/* 검색은 목록 하단 — 훑어보다 못 찾았을 때 꺼내 드는 도구라 아래가 자연스럽다. */}
      <div className="mt-6">
        <TextField
          value={queryInput}
          placeholder="🔍 제목·내용 검색"
          className="py-2.5 text-[14px]"
          onChange={(e) => setQueryInput(e.target.value)}
        />
      </div>
    </>
  );
}
